import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TransactionDetailModal from '../components/TransactionDetailModal';
import InstagramIcon from '../components/InstagramIcon';
import { addPetals, getUserById, updateUserStatus } from '../services/userService';
import { getTransactions } from '../services/paymentService';
import {
  ACCOUNT_STATUS,
  DATING_INTENTIONS,
  DATING_PREFERENCE,
  FAMILY_PLANS,
  GENDER,
  HABIT,
  HAVE_CHILDREN,
  SEXUALITY,
  enumLabel,
} from '../constants/enums';

const PAGE_SIZE = 10;

const PETAL_OPTIONS = [3, 12, 50, 100];

const STATUS_DISPLAY = {
  completed: { label: 'Success', className: 'success' },
  pending: { label: 'Pending', className: 'pending' },
  failed: { label: 'Failed', className: 'failed' },
  refunded: { label: 'Refunded', className: 'refunded' },
};

const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
};

const TRANSACTION_TYPE_TABS = [
  { key: 'walletLedger', label: 'Wallet Ledger' },
  { key: 'purchases', label: 'Purchases' },
];

function calcAge(dateOfBirth) {
  if (!dateOfBirth) return '-';
  const diff = Date.now() - new Date(dateOfBirth).getTime();
  const age = Math.floor(diff / 31557600000);
  return age > 0 && age < 150 ? age : '-';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatPstTime(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles',
  }).toLowerCase()} PST`;
}

function formatLongDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
}

function formatAmount(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount));
}

function getStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function packageLabel(txn) {
  const petals = txn.package?.petalCount ?? txn.petalCount;
  if (petals == null) return 'Petal Pack';
  return petals >= 1000 ? 'Premium Pack' : 'Basic Pack';
}

function getStatus(u) {
  if (u.isBlocked === 1) return 'Blocked';
  if (u.isActive === 0) return 'Inactive';
  return 'Active';
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function textValue(value) {
  if (value == null) return '-';
  const trimmed = String(value).trim();
  return trimmed || '-';
}

function ProfileSection({ title, items }) {
  return (
    <div className="profile-info-group">
      <h5 className="profile-info-group-title">{title}</h5>
      <dl className="profile-info-list">
        {items.map((item) => (
          <div className="profile-info-row" key={item.label}>
            <dt>
              {item.label}
              {item.hidden && <span className="detail-hidden-tag">Hidden</span>}
            </dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState('');
  const [txnPage, setTxnPage] = useState(1);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [txnType, setTxnType] = useState('walletLedger');
  const [viewPurchaseId, setViewPurchaseId] = useState(null);
  const [showAddPetalsModal, setShowAddPetalsModal] = useState(false);
  const [selectedPetals, setSelectedPetals] = useState(null);
  const [addPetalsLoading, setAddPetalsLoading] = useState(false);
  const [addPetalsError, setAddPetalsError] = useState('');
  const [txnRefreshKey, setTxnRefreshKey] = useState(0);

  async function fetchUser() {
    setLoading(true);
    setError('');
    try {
      const response = await getUserById(userId);
      setUser(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch user details.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUser();
    setTxnPage(1);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;

    async function fetchTransactions() {
      setTransactionsLoading(true);
      setTransactionsError('');
      try {
        const response = await getTransactions({
          page: txnPage,
          limit: PAGE_SIZE,
          type: txnType,
          userId,
          sort: 'newest',
        });
        if (cancelled) return;
        const payload = response.data || {};
        const transactionData = payload[txnType];
        setTransactions(Array.isArray(transactionData) ? transactionData : []);
        setTxnTotalPages(response.pagination?.totalPages || 1);

        if (payload.user) {
          setUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              totalPetals: payload.user.totalPetals ?? prev.totalPetals,
              lifetimePetalPurchaseCount:
                payload.user.lifetimePetalPurchaseCount
                ?? prev.lifetimePetalPurchaseCount,
              balance: payload.user.totalPetals ?? prev.balance,
            };
          });
        }
      } catch (err) {
        if (cancelled) return;
        setTransactionsError(err.message || 'Failed to load transactions.');
        setTransactions([]);
        setTxnTotalPages(1);
      } finally {
        if (!cancelled) setTransactionsLoading(false);
      }
    }

    fetchTransactions();
    return () => {
      cancelled = true;
    };
  }, [userId, txnPage, txnType, txnRefreshKey]);

  function goToTxnPage(nextPage) {
    if (nextPage < 1 || nextPage > txnTotalPages || nextPage === txnPage || transactionsLoading) return;
    setTxnPage(nextPage);
  }

  function changeTxnType(type) {
    if (type === txnType) return;
    setTxnPage(1);
    setTxnType(type);
  }

  const txnPageItems = getPageItems(txnPage, txnTotalPages);

  async function confirmToggleBlock() {
    const newStatus = user.isBlocked === 1 ? 'unblocked' : 'blocked';
    setShowBlockModal(false);
    setStatusLoading(true);
    try {
      await updateUserStatus(user.id, newStatus);
      await fetchUser();
    } catch (err) {
      alert(err.message || 'Failed to update user status.');
    } finally {
      setStatusLoading(false);
    }
  }

  function openAddPetalsModal() {
    setSelectedPetals(null);
    setAddPetalsError('');
    setShowAddPetalsModal(true);
  }

  function closeAddPetalsModal() {
    if (addPetalsLoading) return;
    setShowAddPetalsModal(false);
  }

  async function confirmAddPetals() {
    if (!selectedPetals) return;
    setAddPetalsLoading(true);
    setAddPetalsError('');
    try {
      await addPetals(user.id, selectedPetals);
      setShowAddPetalsModal(false);
      await fetchUser();
      setTxnRefreshKey((key) => key + 1);
    } catch (err) {
      setAddPetalsError(err.message || 'Failed to add petals.');
    } finally {
      setAddPetalsLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="user-detail-page main-bg-top">
        <div className="user-detail-loading">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-page main-bg-top">
        <div className="user-detail-error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const status = getStatus(user);
  const profile = user.profile || {};
  const gender = enumLabel(GENDER, profile.gender ?? user.gender);
  const age = calcAge(profile.dateOfBirth ?? user.dateOfBirth);
  const referredBy = user.referredBy;

  const profileSections = [
    {
      title: 'Personal',
      items: [
        { label: 'Gender', value: gender, hidden: profile.isGenderVisibleOnProfile === false },
        { label: 'Age', value: age },
        { label: 'Date of Birth', value: formatDate(profile.dateOfBirth ?? user.dateOfBirth) },
        { label: 'Height', value: textValue(profile.height) },
        { label: 'Location', value: textValue(profile.locationCity ?? user.locationCity) },
        {
          label: 'Hometown',
          value: textValue(profile.homeTown),
          hidden: profile.isHomeTownVisibleOnProfile === false,
        },
      ],
    },
    {
      title: 'Dating Preferences',
      items: [
        {
          label: 'Sexuality',
          value: enumLabel(SEXUALITY, profile.sexuality),
          hidden: profile.isSexualityVisibleOnProfile === false,
        },
        { label: 'Interested In', value: enumLabel(DATING_PREFERENCE, profile.datingPreference) },
        {
          label: 'Intentions',
          value: enumLabel(DATING_INTENTIONS, profile.datingIntentions),
          hidden: profile.isDatingIntentionsOnProfile === false,
        },
        { label: 'Children', value: enumLabel(HAVE_CHILDREN, profile.haveChildren) },
        {
          label: 'Family Plans',
          value: enumLabel(FAMILY_PLANS, profile.familyPlans),
          hidden: profile.isFamilyPlanVisibleOnProfile === false,
        },
      ],
    },
    {
      title: 'Work & Education',
      items: [
        {
          label: 'Work',
          value: textValue(profile.work),
          hidden: profile.isWorkVisibleOnProfile === false,
        },
        {
          label: 'Job Title',
          value: textValue(profile.jobTitle),
          hidden: profile.isJobTitleVisibleOnProfile === false,
        },
        {
          label: 'School',
          value: textValue(profile.school),
          hidden: profile.isSchoolVisibleOnProfile === false,
        },
      ],
    },
    {
      title: 'Lifestyle',
      items: [
        {
          label: 'Drink',
          value: enumLabel(HABIT, profile.drink),
          hidden: profile.isDrinkVisibleOnProfile === false,
        },
        {
          label: 'Smoke Weed',
          value: enumLabel(HABIT, profile.smokeWeed),
          hidden: profile.isSmokeVisibleOnProfile === false,
        },
        {
          label: 'Drugs',
          value: enumLabel(HABIT, profile.drugs),
          hidden: profile.isDrugsVisibleOnProfile === false,
        },
      ],
    },
    {
      title: 'Account & Activity',
      items: [
        { label: 'Petals', value: user.petals ?? 0 },
        { label: 'Joined', value: formatDate(user.createdAt) },
        {
          label: 'Account Status',
          value: enumLabel(ACCOUNT_STATUS, user.accountStatus, 'Not Approved'),
        },
        { label: 'Profile', value: profile.isProfileComplete ? 'Complete' : 'Incomplete' },
        { label: 'Notifications', value: profile.notificationsEnabled ? 'Enabled' : 'Disabled' },
        { label: 'Location Updated', value: formatDate(profile.locationUpdatedAt) },
      ],
    },
  ];

  const signupDetails = user.signupDetails;
  if (signupDetails) {
    const { signupUrl, submittedAt } = signupDetails;

    profileSections.push(
      {
        title: 'Geolocation Details',
        items: [
          { label: 'Country', value: textValue(signupDetails.country) },
          { label: 'State', value: textValue(signupDetails.state) },
          { label: 'City', value: textValue(signupDetails.city) },
          { label: 'Zip Code', value: textValue(signupDetails.zipCode) },
          { label: 'Timezone', value: textValue(signupDetails.timezone) },
        ],
      },
      {
        title: 'Device Details',
        items: [
          { label: 'IP', value: textValue(signupDetails.ipAddress) },
          { label: 'Device', value: textValue(signupDetails.device) },
          { label: 'OS', value: textValue(signupDetails.os) },
          { label: 'Browser', value: textValue(signupDetails.browser) },
          { label: 'User Agent', value: textValue(signupDetails.userAgent) },
          {
            label: 'URL',
            value: signupUrl ? (
              <a href={signupUrl} target="_blank" rel="noopener noreferrer" className="detail-link-btn">
                {signupUrl}
              </a>
            ) : '-',
          },
        ],
      },
      {
        title: 'Submission Details',
        items: [
          { label: 'Day', value: textValue(formatLongDate(submittedAt)) },
          { label: 'Time', value: textValue(formatPstTime(submittedAt)) },
        ],
      },
    );
  }

  if (referredBy) {
    profileSections.push({
      title: 'Referred By',
      items: [
        { label: 'Name', value: textValue(referredBy.name) },
        { label: 'Email', value: textValue(referredBy.email) },
        {
          label: 'Profile',
          value: referredBy.id ? (
            <button
              type="button"
              className="detail-link-btn"
              onClick={() => navigate(`/users/${referredBy.id}`)}
            >
              View referrer
            </button>
          ) : '-',
        },
      ],
    });
  }

  return (
    <div className="user-detail-page main-bg-top">
      <div className="user-detail-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h2>User Details</h2>
      </div>

      <div className="user-detail-content">
        {/* Profile Card */}
        <div className="user-detail-card user-profile-card">
          <div className="user-profile-top">
            <div className="user-detail-avatar">
              {user.images && user.images.length > 0 ? (
                <img src={user.images[0].url} alt={user.name} className="avatar-img avatar-lg" />
              ) : (
                <span className="avatar-placeholder avatar-lg">
                  {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            <div className="user-profile-info">
              <h3 className="user-detail-name">
                {user.name || '-'}
                {user.instagramUrl && (
                  <a
                    href={user.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="user-instagram-link"
                    title="Open Instagram profile"
                  >
                    <InstagramIcon />
                    {(user.instagramUsername || user.profileInsta) && (
                      <span className="user-instagram-username">
                        @{user.instagramUsername || user.profileInsta}
                      </span>
                    )}
                  </a>
                )}
              </h3>
              <p className="user-detail-email">{user.email || '-'}</p>
              <div className="user-profile-actions">
                <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
                <button
                  className={`btn ${user.isBlocked === 1 ? 'btn-primary' : 'btn-danger'} btn-sm`}
                  onClick={() => setShowBlockModal(true)}
                  disabled={statusLoading}
                >
                  {statusLoading ? 'Updating...' : user.isBlocked === 1 ? 'Unblock User' : 'Block User'}
                </button>
              </div>
            </div>
          </div>

          <div className="user-balance-stats">
            {/* {status === 'Active' && (
              <button
                type="button"
                className="btn btn-primary btn-sm add-petals-btn"
                onClick={openAddPetalsModal}
              >
                Add Petals
              </button>
            )} */}
            <div className="user-current-balance">
              <span className="user-current-balance-label">Current Petal Balance</span>
              <div className="user-current-balance-value">
                <img
                  alt="Petals"
                  className="menu-icon-img"
                  src={`${process.env.PUBLIC_URL}/images/petals.png`}
                />
                <span>
                  {(user.balance ?? user.currentBalance ?? user.totalPetals ?? user.petals ?? 0).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="user-current-balance">
              <span className="user-current-balance-label">Lifetime Purchased Petals</span>
              <div className="user-current-balance-value">
                <img
                  alt="Petals"
                  className="menu-icon-img"
                  src={`${process.env.PUBLIC_URL}/images/petals.png`}
                />
                <span>
                  {(user.lifetimePetalPurchaseCount ?? 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Profile */}
        <div className="user-detail-card">
          <div className="user-detail-section-header">
            <h4 className="detail-card-title">Profile</h4>
          </div>

          {user.images && user.images.length > 0 && (
            <div className="user-images-grid user-images-grid-compact">
              {user.images.map((img) => (
                <div key={img.id} className="user-image-item">
                  <img src={img.url} alt="Profile" />
                </div>
              ))}
            </div>
          )}

          <div className="profile-info-grid">
            {profileSections.map((section) => (
              <ProfileSection key={section.title} title={section.title} items={section.items} />
            ))}
          </div>
        </div>

        {/* Transactions */}
        <div className="user-detail-card">
          <div className="user-detail-section-header">
            <h4 className="detail-card-title">Transactions</h4>
            <div className="chart-tabs" role="tablist" aria-label="Transaction type filters">
              {TRANSACTION_TYPE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={txnType === tab.key}
                  className={`chart-tab${txnType === tab.key ? ' active' : ''}`}
                  onClick={() => changeTxnType(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="users-table-wrap user-detail-transactions">
            <table className="users-table payments-table">
              <thead>
                {txnType === 'walletLedger' ? (
                  <tr>
                    <th>Transaction ID</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Direction</th>
                    <th>Balance Before</th>
                    <th>Balance After</th>
                    <th>Reference Type</th>
                    <th>Date & Time</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Transaction ID</th>
                    <th>Package</th>
                    <th>Petals</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                    <th>Date & Time</th>
                    <th>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {transactionsLoading ? (
                  <tr>
                    <td colSpan="8" className="users-empty">Loading transactions...</td>
                  </tr>
                ) : transactionsError ? (
                  <tr>
                    <td colSpan="8" className="users-empty">{transactionsError}</td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="users-empty">
                      No {txnType === 'walletLedger' ? 'wallet ledger entries' : 'purchases'} found for this user.
                    </td>
                  </tr>
                ) : txnType === 'walletLedger' ? (
                  transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td className="txn-id-cell">{txn.id || '—'}</td>
                      <td>{txn.type ? txn.type.charAt(0).toUpperCase() + txn.type.slice(1) : '—'}</td>
                      <td>
                        <div className="petals-cell">
                          <img
                            alt="Petals"
                            className="menu-icon-img"
                            src={`${process.env.PUBLIC_URL}/images/petals.png`}
                          />
                          {txn.amount ?? '—'}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${txn.direction === 'credit' ? 'success' : 'failed'}`}>
                          {txn.direction
                            ? txn.direction.charAt(0).toUpperCase() + txn.direction.slice(1)
                            : '—'}
                        </span>
                      </td>
                      <td>{txn.balanceBefore ?? '—'}</td>
                      <td>{txn.balanceAfter ?? '—'}</td>
                      <td>{txn.referenceType?.replaceAll('_', ' ') || '—'}</td>
                      <td>
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(txn.createdAt)}</span>
                          <span className="datetime-time">{formatTime(txn.createdAt)}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  transactions.map((txn) => {
                    const txnStatus = getStatusDisplay(txn.status);
                    const packagePetals = txn.package?.petalCount ?? txn.petalCount ?? 0;
                    const platform = PLATFORM_LABELS[txn.platform] || txn.platform || '—';

                    return (
                      <tr key={txn.id}>
                        <td className="txn-id-cell">{txn.transactionId || '—'}</td>
                        <td>
                          <div className="package-cell">
                            <span className="package-name">
                              {packageLabel(txn)}{' '}
                              <img
                                alt=""
                                className="menu-icon-img"
                                src={`${process.env.PUBLIC_URL}/images/star.svg`}
                              />
                            </span>
                            <span className="package-petals">{packagePetals} Petals</span>
                          </div>
                        </td>
                        <td>{txn.petalCount ?? '—'}</td>
                        <td>{formatAmount(txn.price)}</td>
                        <td>{platform}</td>
                        <td>
                          <span className={`status-badge status-${txnStatus.className}`}>{txnStatus.label}</span>
                        </td>
                        <td>
                          <div className="datetime-cell">
                            <span className="datetime-date">{formatDate(txn.createdAt)}</span>
                            <span className="datetime-time">{formatTime(txn.createdAt)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button
                              className="action-btn"
                              title="View"
                              type="button"
                              onClick={() => setViewPurchaseId(txn.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!transactionsLoading && !transactionsError && txnTotalPages > 1 && (
            <div className="payments-pagination user-detail-txn-pagination" aria-label="Transactions pagination">
              <button
                type="button"
                className="pagination-page-btn"
                disabled={txnPage <= 1}
                onClick={() => goToTxnPage(txnPage - 1)}
                aria-label="Previous page"
              >
                ‹
              </button>

              {txnPageItems.map((item, index) =>
                item === '…' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`pagination-page-btn ${txnPage === item ? 'active' : ''}`}
                    onClick={() => goToTxnPage(item)}
                    aria-current={txnPage === item ? 'page' : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                className="pagination-page-btn"
                disabled={txnPage >= txnTotalPages}
                onClick={() => goToTxnPage(txnPage + 1)}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {showBlockModal && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{user.isBlocked === 1 ? 'Unblock User?' : 'Block User?'}</h3>
            <p className="modal-message">
              Are you sure you want to {user.isBlocked === 1 ? 'unblock' : 'block'} <br />
              {user.name}?
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => setShowBlockModal(false)}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={confirmToggleBlock}>
                {user.isBlocked === 1 ? 'Unblock' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPetalsModal && (
        <div className="modal-overlay" onClick={closeAddPetalsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Add Petals</h3>
            <p className="modal-message">
              Choose how many petals to add to <br />
              {user.name}'s balance.
            </p>
            <div className="petal-options" role="radiogroup" aria-label="Petal amount">
              {PETAL_OPTIONS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  role="radio"
                  aria-checked={selectedPetals === amount}
                  className={`petal-option${selectedPetals === amount ? ' active' : ''}`}
                  onClick={() => setSelectedPetals(amount)}
                  disabled={addPetalsLoading}
                >
                  <img
                    alt=""
                    className="menu-icon-img"
                    src={`${process.env.PUBLIC_URL}/images/petals.png`}
                  />
                  {amount}
                </button>
              ))}
            </div>
            {addPetalsError && <p className="petal-options-error">{addPetalsError}</p>}
            <div className="modal-actions">
              <button
                className="modal-btn modal-btn-cancel"
                onClick={closeAddPetalsModal}
                disabled={addPetalsLoading}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn-confirm"
                onClick={confirmAddPetals}
                disabled={!selectedPetals || addPetalsLoading}
              >
                {addPetalsLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      <TransactionDetailModal
        open={Boolean(viewPurchaseId)}
        purchaseId={viewPurchaseId}
        onClose={() => setViewPurchaseId(null)}
      />
    </div>
  );
}
